import {ResourcePageState} from '../../core/services/resource-page-state.service';
import {Location} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Router} from '@angular/router';
import {StaticPageRestService} from '../../api/services/static-page.rest.service';
import {StaticPageApiModel} from '../../api/models/static-page.api.model';
import {SeoService} from '../../core/services/seo.service';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-static-page',
  templateUrl: './static-page.component.html',
  styleUrls: ['./static-page.component.scss']
})
export class StaticPageComponent implements OnInit, OnDestroy {
  public page: StaticPageApiModel | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    public readonly resourcePage: ResourcePageState,
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly staticPageRS: StaticPageRestService,
    private readonly seo: SeoService,
    private readonly translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.push(this.resourcePage.retry$.subscribe(() => this.loadPage()));
    this.subscriptions.push(this.route.data.subscribe(data => {
      this.page = data['page'] || null;
      if (this.page) {
        this.applySeo(this.page);
        this.syncCanonicalRoute(this.page);
      }
    }));

    this.subscriptions.push(this.translate.onLangChange.subscribe(() => this.loadPage()));
  }

  ngOnDestroy(): void {
    this.resourcePage.reset();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public goBack(): void {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this.location.back();
      return;
    }

    void this.router.navigate(['/']);
  }

  private loadPage(): void {
    const pageSlug = this.page?.slug || this.route.snapshot.data['slug'] || this.route.snapshot.paramMap.get('slug');
    if (!pageSlug) { this.resourcePage.show('404'); return; }
    const bySlug = !!this.page || this.route.snapshot.data['lookup'] === 'slug';
    this.subscriptions.push(this.resourcePage.load(pageSlug, options => bySlug
      ? this.staticPageRS.getBySlug(pageSlug, {...options, bypassCache: true})
      : this.staticPageRS.getByPublicSlug(pageSlug, {...options, bypassCache: true})).subscribe(page => {
        this.page = page;
        this.applySeo(page);
        this.syncCanonicalRoute(page);
      }));
  }

  private applySeo(page: StaticPageApiModel): void {
    const title = page.metaTitle || page.title;
    const description = page.metaDescription || page.excerpt || this.translate.instant('meta.description');
    const path = this.staticPageRS.buildPublicRoute(page);

    this.seo.setTitle(title);
    this.seo.setDescription(description || '');
    this.seo.setOpenGraph({title, description: description || '', url: this.seo.getAbsoluteUrl(path), type: 'article'});
    this.seo.setCanonical(path);
    this.seo.setIndexable(true);
  }

  private syncCanonicalRoute(page: StaticPageApiModel): void {
    const targetPath = this.staticPageRS.buildPublicRoute(page);
    const currentPath = this.router.url.split('?')[0];
    const shouldRedirect = !!this.route.snapshot.data['canonicalRedirect'];

    if ((shouldRedirect || currentPath.startsWith('/pages/')) && currentPath !== targetPath) {
      void this.router.navigateByUrl(targetPath, {replaceUrl: true});
    }
  }
}
